import {
  BackButton,
  ConfirmNotification,
  DataGridActionsColumn,
  StatusDropdown,
  TableView1,
  useStatusColumn,
} from "@/components";
import React, { useContext, useEffect, useMemo, useState } from "react";
import type { TypedDocumentNode } from "@apollo/client";
import {
  GET_ALL_PARTNERS,
  TENANTS_LIST,
  useDeleteTenant,
  useHasPermission,
  usePageTitle,
  useUpdateTenant,
} from "@/hooks";
import { useTextDomainContext, useUser } from "@/app//providers";
import {
  getRoleTypeNameFromUser,
  permissionsCompaniesReadonly,
  permissionsTenantsFull,
  permissionsUsersReadonly,
} from "@/utils";
import type { QueryGetPartnersArgs, QueryTenantsListArgs } from "@/types";
import { Box, Button, Chip, Stack } from "@mui/material";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { AddTenant } from "../";
import { useLazyQuery, useQuery } from "@apollo/client/react";
import { useSnackbar } from "notistack";
import {
  DialogParameters,
  PartnersResponse,
  StatusOption,
  Tenant,
  TenantListProps,
  TenantsListResponse,
} from "./types";

const feature = "common";

export const TenantList: React.FC<TenantListProps> = (props) => {
  //#region Permissions
  const { allow: allowCompaniesReadonly } = useHasPermission(
    permissionsCompaniesReadonly,
    feature,
  );
  const { allow: allowUsersReadonly } = useHasPermission(
    permissionsUsersReadonly,
    feature,
  );
  const { allow: allowTenantsFull } = useHasPermission(
    permissionsTenantsFull,
    feature,
  );
  //#endregion Perissions

  const navigate = useNavigate();
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const location = useLocation();
  const { user } = useUser();
  const { partnerId: parmsPartnerId = "" } = useParams();
  const parentPath = location?.state?.parentPath || props?.parentPath || "";
  const roleType = getRoleTypeNameFromUser(user);

  // Set page title
  usePageTitle(gettext("Corporate Groups"));

  const partnerId = parmsPartnerId || user?.currentPartnerId || "";

  // Destructure props directly instead of using state
  const {
    showAdd = false,
    showEdit = false,
    showDelete = false,
    showSettings = true,
    // showFeatures = false,
  } = props;

  const getAllPartnersQuery = GET_ALL_PARTNERS as TypedDocumentNode<
    PartnersResponse,
    QueryGetPartnersArgs
  >;
  const tenantsListQuery = TENANTS_LIST as TypedDocumentNode<
    TenantsListResponse,
    QueryTenantsListArgs
  >;

  const [getPartners] = useLazyQuery(getAllPartnersQuery);
  const { deleteTenant, loading: deleting } = useDeleteTenant(
    () => {
      enqueueSnackbar(
        gettext("Corporate Group has been deleted successfully."),
        {
          variant: "success",
        },
      );
      // Refetch tenants list
      refetchTenants();
    },
    (error) => {
      enqueueSnackbar(error || gettext("Failed to delete Corporate Group"), {
        variant: "error",
      });
    },
  );
  const { updateTenant, loading: updating } = useUpdateTenant();
  const { enqueueSnackbar } = useSnackbar();

  const queryVariables = useMemo((): QueryTenantsListArgs => {
    let queryPartnerId = partnerId;

    if (!queryPartnerId) {
      if (parentPath.includes("partner-admin")) {
        queryPartnerId = "none";
      } else if (roleType === "company" || roleType === "partner") {
        queryPartnerId = null;
      }
    }

    const variables: QueryTenantsListArgs = {};
    if (queryPartnerId) {
      variables.partnerId = queryPartnerId;
    } else if (roleType === "partner" && user?.currentPartnerId) {
      variables.partnerId = user.currentPartnerId;
    }

    if (roleType === "company" && user?.email) {
      variables.email = user.email;
    }

    return variables;
  }, [partnerId, parentPath, roleType, user?.currentPartnerId, user?.email]);

  const {
    data: tenantsData,
    loading,
    refetch: refetchTenants,
  } = useQuery(tenantsListQuery, {
    variables: queryVariables,
    notifyOnNetworkStatusChange: true,
  });

  // Use the reusable status column hook
  const { statusColumn, StatusMenu } = useStatusColumn({
    onStatusChange: (row: Tenant, newStatus: string) => {
      updateTenant({
        variables: {
          tenantId: row.id,
          status: newStatus,
        },
        refetchQueries: ["TenantsList"],
        awaitRefetchQueries: true,
      })
        .then((response) => {
          if (response?.data?.updateTenant?.success) {
            enqueueSnackbar(gettext("Status updated successfully"), {
              variant: "success",
            });
          } else {
            enqueueSnackbar(gettext("Failed to update status"), {
              variant: "error",
            });
          }
        })
        .catch(() => {
          enqueueSnackbar(gettext("Error updating status"), {
            variant: "error",
          });
        });
    },
    updating,
  });

  //region React Hook (useState)

  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [dialogParameters, setDialogParameters] = useState<DialogParameters>(
    {} as DialogParameters,
  );
  const [addFormOpen, setAddFormOpen] = useState<boolean>(false);
  const [editRow, setEditRow] = useState<Partial<Tenant>>({});
  // const [loading, setLoading] = useState(false);
  const [partnerName, setPartnerName] = useState<string>("");
  const [inputStatus, setInputStatus] = useState<string | StatusOption>("all");
  // const [tenantsData, setTenantsData] = useState([]);

  //endregion React Hook (useState)

  //#region useMemo

  const showItems = useMemo(() => {
    return {
      showEdit: (showEdit && allowTenantsFull) ?? false,
      showDelete: (showDelete && allowTenantsFull) ?? false,
      // showFeatures: showFeatures ?? false,
      showUsers: allowUsersReadonly,
      showCompanies: allowCompaniesReadonly,
      showSettings: showSettings ?? true,
    };
  }, [
    showEdit,
    showDelete,
    showSettings,
    allowTenantsFull,
    allowUsersReadonly,
    allowCompaniesReadonly,
  ]);

  const showActionsColumn = useMemo(() => {
    // const { showUsers, showCompanies, ...filteredShowItems } = showItems;
    // void showUsers;
    // void showCompanies;
    // return Object.values(filteredShowItems).some((item) => item === true);
    return Object.values(showItems).some((item) => item === true);
  }, [showItems]);

  const rows = useMemo((): Tenant[] => {
    let returnValue: Tenant[] =
      tenantsData?.tenantsList
        ?.filter((tenant): tenant is NonNullable<typeof tenant> =>
          Boolean(tenant),
        )
        .map((tenant): Tenant => ({
          ...tenant,
          subscriptionPlanName:
            tenant.subscriptionPlanName || tenant.subscriptionPlan,
          usersCount: tenant.users?.length || 0,
          companiesCount: tenant.companies?.length || 0,
        })) || [];
    const statusValue =
      (inputStatus as StatusOption)?.value || inputStatus || "";

    if (statusValue && statusValue !== "all") {
      if (statusValue === "InActive") {
        const now = new Date();
        returnValue = returnValue.filter((row) => {
          const expireDate = new Date(row.subscriptionExpireAt);
          return row.status === statusValue || expireDate < now;
        });
      } else {
        returnValue = returnValue.filter((row) => row.status === statusValue);
      }
    }

    return returnValue;
  }, [inputStatus, tenantsData]);

  //region React Hook (useEffect)

  useEffect(() => {
    if (partnerId) {
      getPartners({
        variables: {
          partnerId,
        },
      }).then((res) => {
        const pName = res?.data?.getPartners[0]?.name;
        if (pName && pName !== partnerName) {
          setPartnerName(pName);
        }
      });
    }
  }, [getPartners, partnerId, partnerName]);

  //endregion React Hook (useEffect)

  //#region Handlers

  const menuClick = (action: string, row: Tenant): void => {
    switch (action) {
      case "tenant-edit":
        handleEdit(row);
        break;
      case "tenant-delete":
        handleOnClickDeleteSingle(row);
        break;
      case "tenant-features":
        handleOnClickFeatures(row);
        break;
      case "tenant-users":
        navigate(`/${parentPath}/tenants/${row.id}/users`, {
          state: { parentPath: location.pathname },
        });
        break;
      case "tenant-companies":
        navigate(`/${parentPath}/tenants/${row.id}/companies`, {
          state: { parentPath: location.pathname },
        });
        break;
      case "tenant-settings":
        navigate(`/${parentPath}/tenants/${row.id}/settings`, {
          state: { parentPath: location.pathname },
        });
        break;
      default:
        break;
    }
  };

  // Compute derived values
  const title =
    partnerId && partnerName
      ? gettext("Corporate Groups for the partner ") + partnerName
      : gettext("Corporate Groups");

  const initialState = {
    pagination: {
      paginationModel: {
        page: 0,
        pageSize: 100,
      },
    },
    columns: {
      columnVisibilityModel: {
        id: false,
        name: true,
        key: true,
        createdAt: false,
        updatedAt: false,
      },
    },
  };

  const columns = [
    {
      field: "id",
      headerName: gettext("ID"),
      checkboxSelection: true,
    },
    {
      field: "name",
      headerName: gettext("Name"),
      flex: 1,
      // minWidth: 150,
      headerAlign: "left",
      type: "string",
    },
    {
      field: "partnerName",
      headerName: gettext("Partners"),
      flex: 1,
      // minWidth: 150,
      headerAlign: "left",
      type: "string",
      valueGetter: (_value: any, row: Tenant) => {
        if (row?.partnerNames && row.partnerNames.length > 0) {
          return row.partnerNames.join(", ");
        }
        return row?.partnerName || "";
      },
      renderCell: (params: any) => {
        const names: string[] =
          params.row?.partnerNames && params.row.partnerNames.length > 0
            ? params.row.partnerNames
            : params.row?.partnerName
              ? [params.row.partnerName]
              : [];

        if (names.length === 0) {
          return gettext("No Partner");
        }
        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              alignItems: "center",
              py: 0.5,
            }}
          >
            {names.map((name: string, index: number) => (
              <Chip key={index} label={name} size="small" variant="outlined" />
            ))}
          </Box>
        );
      },
    },
    {
      field: "dataLimitName",
      headerName: gettext("Historical Data"),
      flex: 1,
      // minWidth: 150,
      headerAlign: "left",
      type: "string",
      renderCell: (params: { value: string }) => {
        return params.value ? params.value : gettext("No Limit");
      },
    },
    {
      field: "subscriptionPlanName",
      headerName: gettext("Subscription"),
      flex: 1,
      // minWidth: 150,
      headerAlign: "left",
      type: "string",
    },
    {
      field: "subscriptionExpireAt",
      headerName: gettext("Expire At"),
      headerAlign: "left",
      flex: 1,
      type: "date",
      valueGetter: (value: string | Date | null) => value && new Date(value),
    },
    ...(roleType !== "company" ? [statusColumn] : []),
    ...(allowUsersReadonly
      ? [
          {
            field: "usersCount",
            headerName: gettext("Users"),
            maxWidth: 100,
            headerAlign: "center",
            type: "number",
            align: "center",
            renderCell: (params: { value: number; row: Tenant }) => (
              <Button
                variant="outlined"
                component={Link}
                to={`/${props.parentPath}/tenants/${params.row.id}/users`}
                state={{ parentPath: location.pathname }}
              >
                {params.value}
              </Button>
            ),
          },
        ]
      : []),
    ...(allowCompaniesReadonly
      ? [
          {
            field: "companiesCount",
            headerName: gettext("Companies"),
            maxWidth: 100,
            headerAlign: "center",
            type: "number",
            align: "center",
            renderCell: (params: { value: number; row: Tenant }) => (
              <Button
                variant="outlined"
                component={Link}
                to={`/${props.parentPath}/tenants/${params.row.id}/companies`}
                state={{ parentPath: location.pathname }}
              >
                {params.value}
              </Button>
            ),
          },
        ]
      : []),
    ...DataGridActionsColumn<string, Tenant>(
      showActionsColumn,
      "tenant",
      showItems,
      menuClick,
    ),
  ];

  //region Handlers (continued)
  const handleEdit = (row: Tenant): void => {
    setEditRow(row);
    setAddFormOpen(true);
  };

  const handleOnClickDeleteSingle = (row: Tenant): void => {
    setDialogParameters({
      action: "delete-single",
      id: row.id,
    });
    setShowConfirm(true);
  };

  const handleOnClickDeleteMultiple = (ids: string[]): void => {
    setDialogParameters({
      action: "delete-multi",
      ids: ids,
    });
    setShowConfirm(true);
  };

  const handleGetResult = (obj: {
    result: string;
    parameters: DialogParameters;
  }): void => {
    if (obj.result === "yes") {
      deleteTenant({
        variables: {
          id: obj?.parameters?.id,
        },
        refetchQueries: [{ query: TENANTS_LIST }],
      });
    }
    setShowConfirm(false);
  };

  const handleOnClickFeatures = (row: Tenant): void => {
    const key = row.key;
    navigate(`/${parentPath}/subscriptions/features/${key}`);
  };

  const handleStatusChange = (event: string | StatusOption): void => {
    setInputStatus(event);
  };

  const postToolbar = (): React.ReactElement => {
    return (
      <>
        <Stack direction="row" spacing={1} sx={{ pl: 1, alignItems: "center" }}>
          <StatusDropdown
            onChange={handleStatusChange}
            defaultValue={
              typeof inputStatus === "string" ? inputStatus : inputStatus?.value
            }
            showAll
            required
            visualStyle="autoComplete"
          />

          {parentPath && <BackButton link={parentPath} />}

          {showAdd && allowTenantsFull && (
            <Button
              variant="contained"
              onClick={() => {
                setAddFormOpen(true);
              }}
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {gettext("Add Corporate Group")}
            </Button>
          )}
        </Stack>
      </>
    );
  };

  const handleCloseAddForm = (event: boolean): void => {
    setAddFormOpen(event);
    setEditRow({});
  };
  //endregion Handlers

  return (
    <>
      <TableView1
        title={title}
        rows={rows}
        // rows={[]}
        columns={columns}
        loading={loading}
        checkboxSelection={false}
        initialState={initialState}
        // preToolbar={preToolbar()}
        postToolbar={postToolbar()}
        // headerRight={headerRight()}
        showGridToolbar={true}
        showGridToolbarDeleteButton={false}
        showGridToolbarDensitySelector={false}
        showGridToolbarColumnsButton={false}
        showGridToolbarQuickFilter
        showGridToolbarFilterButton={false}
        showGridToolbarExport={false}
        // onRowSelectionModelChange={handleOnRowSelectionModelChange}
        onClickDeleteMultiple={handleOnClickDeleteMultiple}
        getRowClassName={(params: { row: Tenant }) => {
          const now = new Date();
          const expireDate = new Date(params.row.subscriptionExpireAt);
          let returnValue = "";

          if (expireDate < now || params.row.status !== "Active") {
            returnValue = "danger-row";
          }
          return returnValue;
        }}
      />

      <StatusMenu />

      <AddTenant
        open={addFormOpen}
        handleClose={handleCloseAddForm}
        row={editRow}
      />

      <ConfirmNotification
        isOpen={showConfirm}
        parameters={dialogParameters}
        getResult={handleGetResult}
      />
      {deleting ? (
        <div className="position-absolute bg-dark opacity-50 w-100 h-100 d-flex justify-content-center align-items-center">
          {/*<Spinner animation="border" variant="light" />*/}
        </div>
      ) : null}
    </>
  );
};
